import React from 'react';
import classnames from 'classnames';
import { useTranslation } from 'react-i18next';
import { calculateRanges } from './helpers';
import ArrowLeft from '@wix/da-ds/pkg/Icons/ArrowLeft';
import ArrowRight from '@wix/da-ds/pkg/Icons/ArrowRight';
import { IconSize } from '@wix/da-ds/pkg/Icons/IconWrap';
import Page from './_partials/Page';
import Dots from './_partials/Dots';
import { BiData, PageNavigationClickBiEvent } from '@wix/da-bi/pkg/events';
import s from './Pagination.scss';

export interface Props {
  currentPage: number;
  totalPages: number;
  pageSize: number;
  showPageNumbers?: boolean;
  getPageUrl?: (page: number) => string;
  onChangePage?: (newPage: number) => void;
}

const Pagination: React.FC<React.PropsWithChildren<Props>> = ({
  currentPage,
  totalPages,
  pageSize,
  showPageNumbers = true,
  onChangePage,
  getPageUrl,
}) => {
  const { t } = useTranslation();

  if (totalPages < 2) {
    return null;
  }

  const { startingBlock, showStartDots, midBlock, showEndDots, endBlock } =
    calculateRanges(currentPage, totalPages);

  const renderButton = (n: number) => (
    <Page
      key={n}
      page={n}
      isCurrent={n === currentPage}
      getPageUrl={getPageUrl}
      onChangePage={onChangePage}
      biData={BiData<PageNavigationClickBiEvent>({
        evid: 113,
        direction: `${n}`,
        thumbs_per_page: pageSize,
      })}
    >
      {n}
    </Page>
  );

  return (
    <div className={s['root']}>
      {currentPage > 1 && (
        <Page
          page={currentPage - 1}
          isCurrent={false}
          onChangePage={onChangePage}
          getPageUrl={getPageUrl}
          biData={BiData<PageNavigationClickBiEvent>({
            evid: 113,
            direction: 'previous',
            thumbs_per_page: pageSize,
          })}
        >
          <ArrowLeft
            size={IconSize.MEDIUM}
            disableDefaultColors
            className={classnames(s['arrow'], s['prev'])}
          />
          {t('pagination.prev')}
        </Page>
      )}
      {showPageNumbers && (
        <>
          {startingBlock.map(renderButton)}
          {showStartDots && (
            <Dots
              pageSize={pageSize}
              totalPages={totalPages}
              onChangePage={onChangePage}
              getPageUrl={getPageUrl}
            />
          )}
          {midBlock.map(renderButton)}
          {showEndDots && (
            <Dots
              pageSize={pageSize}
              totalPages={totalPages}
              onChangePage={onChangePage}
              getPageUrl={getPageUrl}
            />
          )}
          {endBlock.map(renderButton)}
        </>
      )}
      {currentPage < totalPages && (
        <Page
          page={currentPage + 1}
          isCurrent={false}
          onChangePage={onChangePage}
          getPageUrl={getPageUrl}
          biData={BiData<PageNavigationClickBiEvent>({
            evid: 113,
            direction: 'next',
            thumbs_per_page: pageSize,
          })}
        >
          {t('pagination.next')}
          <ArrowRight
            size={IconSize.MEDIUM}
            disableDefaultColors
            className={classnames(s['arrow'], s['next'])}
          />
        </Page>
      )}
    </div>
  );
};
Pagination.displayName = 'Pagination';

export default Pagination;
